import { useDispatch, useSelector } from 'react-redux';
import { UI_FILTER } from '@bwoty-web/ui-kit/icons';
import { useIsGlobetrotter, useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { Button, FilterFooter, Icon, PopoutMenu } from '@bwoty-web/ui-kit';

import FilterGroup from './FilterGroup';
import { filterActiveSelector, filterMatchesSelector } from '../../../redux/selectors/cruiseList/filterSelectors';

import { filterPanelToggled, filtersCleared } from '../../../redux/reducers/cruiseList/filter';
import { selectFilterGroups, selectFilterPanelIsVisible, selectLabels } from '../../../redux/selectors/cruiseList';

import './cruiseFilterPanel.scss';

// No props, one `useSelector` per field. `FilterGroup` builds its own `filterActions` from
// `dispatch`, so it is not passed down.
const CruiseFilterPanel = () => {
  const dispatch = useDispatch();
  const filterActive = useSelector(filterActiveSelector);
  const filterGroups = useSelector(selectFilterGroups);
  const filterMatches = useSelector(filterMatchesSelector);
  const filterPanelIsVisible = useSelector(selectFilterPanelIsVisible);
  const labels = useSelector(selectLabels);

  const isGlobe = useIsGlobetrotter();
  const isMobile = useIsMobile();
  const filterGroupsMarkup = Object.values(filterGroups)
    .map((filterGroup) => {
      const {
        heading,
        id,
        maxNumberOfDestinations: maxDest,
        maxValue,
        minNumberOfDestinations: minDest,
        minValue,
        options,
        order,
        selected,
        selectedMaxNumberOfDestinations: selectedMaxDest,
        selectedMinNumberOfDestinations: selectedMinDest,
      } = filterGroup;

      const minMaxSame = minValue != null && maxValue != null && minValue === maxValue;
      let hasLessThanTwoOptions = !options || options?.length < 2;
      if (options === undefined && !minMaxSame) hasLessThanTwoOptions = false;
      if (hasLessThanTwoOptions || minMaxSame) return null;

      let amountOfActiveFilters = selected?.length;
      if (amountOfActiveFilters === undefined) amountOfActiveFilters = filterActive[id] ? 1 : 0;
      if (
        id === 'destination' &&
        filterActive[id] &&
        selectedMinDest &&
        selectedMaxDest &&
        (selectedMinDest !== minDest || selectedMaxDest !== maxDest)
      )
        amountOfActiveFilters += 1;

      const filterTitle = `${heading}${amountOfActiveFilters > 0 ? ` (${amountOfActiveFilters})` : ''}`;

      return {
        id,
        title: filterTitle,
        content: (
          <FilterGroup
            filterGroup={filterGroup}
            filterMatches={filterMatches}
            filterPanelIsOpen={filterPanelIsVisible}
            filterStopsAllLabel={labels.filter_stops_all}
            key={order}
          />
        ),
      };
    })
    .filter((f) => !!f)
    .sort((a, b) => a.key - b.key);

  const anyFilterActive = Object.values(filterActive).some((isActive) => isActive === true);

  const footerContent = (
    <FilterFooter
      onClearFilter={() => dispatch(filtersCleared())}
      onShowTrip={() => dispatch(filterPanelToggled(false))}
      disableClearFilter={!anyFilterActive}
    />
  );

  const numberOfActiveFilters = Object.values(filterActive).filter((isActive) => isActive === true).length;

  const filterBtnElement = document.getElementById('iw-cruise-list__filter-button');

  const filterBtnProps = { ...(isMobile && { 'aria-haspopup': 'dialog' }) };
  return (
    <div className="iw-cruise-list__filter-container">
      <Button
        className="iw-cruise-list__filter-btn"
        id="iw-cruise-list__filter-button"
        onClick={() => dispatch(filterPanelToggled(!filterPanelIsVisible))}
        variant={isGlobe ? 'secondary' : 'secondary-dark'}
        iconBefore={
          <Icon
            size="xs"
            sizeMobile="xs"
            alt=""
            path={UI_FILTER}
          />
        }
        {...filterBtnProps}
      >
        {`${labels.filterBtnLabel}${numberOfActiveFilters > 0 ? ` (${numberOfActiveFilters})` : ''}`}
      </Button>
      <PopoutMenu
        className="iw-cruise-filter-popout-menu"
        drawerHeight={60}
        footerContent={footerContent}
        menuItems={filterGroupsMarkup}
        onClose={() => dispatch(filterPanelToggled(false))}
        show={filterPanelIsVisible}
        title={labels.filterHeading}
        triggerElements={[filterBtnElement]}
      />
    </div>
  );
};

export default CruiseFilterPanel;
